import React, { useState } from 'react';
import { useTranslation } from '../../i18n';
import { isAxiosError } from '../../types/error';
import { MAX_SUGGESTION_TITLE_LENGTH, MAX_SUGGESTION_MESSAGE_LENGTH, MIN_SUGGESTION_MESSAGE_LENGTH, TOAST_DISPLAY_DURATION } from '../../constants';
import Icon, { IconName } from '../Icon';
import LoadingSpinner from '../LoadingSpinner';
import { SuggestionData } from '../../services/suggestionService';
import './SuggestionModal.css';
import Modal from '../Modal';

interface SuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SuggestionData) => Promise<void>;
}

type SuggestionType = SuggestionData['type'];

const SuggestionModal: React.FC<SuggestionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<SuggestionType>('feature');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeOptions: { value: SuggestionType; icon: IconName; color: string }[] = [
    { value: 'feature', icon: 'rocket', color: 'var(--color-brand)' },
    { value: 'improvement', icon: 'lightbulb', color: '#10b981' },
    { value: 'bug', icon: 'bug', color: '#ef4444' },
    { value: 'general', icon: 'suggestion', color: '#8b5cf6' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !message.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      await onSubmit({ type, title: title.trim(), message: message.trim() });
      setIsSuccess(true);
      // Reset form after success
      setTimeout(() => {
        handleClose();
      }, TOAST_DISPLAY_DURATION);
    } catch (err: unknown) {
      const errorCode = isAxiosError(err) ? (err.response?.data?.error || 'UNKNOWN_ERROR') : 'UNKNOWN_ERROR';
      setError(t(`suggestion.errors.${errorCode}`) || t('suggestion.errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setType('feature');
      setTitle('');
      setMessage('');
      setError(null);
      setIsSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      bare
      isOpen={isOpen}
      onClose={handleClose}
      className="modal-backdrop suggestion-backdrop"
      contentClassName="suggestion-modal"
    >
      <div className="suggestion-modal__header">
        <div className="suggestion-modal__title-group">
          <span className="suggestion-modal__icon"><Icon name="suggestion" /></span>
          <div>
            <h2>{t('suggestion.title')}</h2>
            <p className="suggestion-modal__subtitle">{t('suggestion.subtitle')}</p>
          </div>
        </div>
        <button
          className="suggestion-modal__close"
          onClick={handleClose}
          disabled={isLoading}
          aria-label={t('common.close')}
        >
          <Icon name="close" />
        </button>
      </div>

      {isSuccess ? (
        <div className="suggestion-modal__success">
          <div className="success-animation">
            <div className="success-checkmark">
              <Icon name="circleCheck" />
            </div>
          </div>
          <h3>{t('suggestion.success.title')}</h3>
          <p>{t('suggestion.success.message')}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="suggestion-modal__form">
          {/* Type Selection */}
          <div className="form-group">
            <label>{t('suggestion.labels.type')}</label>
            <div className="type-selector">
              {typeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`type-option ${type === option.value ? 'active' : ''}`}
                  onClick={() => setType(option.value)}
                  style={{ '--type-color': option.color } as React.CSSProperties}
                  disabled={isLoading}
                >
                  <span className="type-option__icon"><Icon name={option.icon} /></span>
                  <span className="type-option__label">{t(`suggestion.types.${option.value}`)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title Input */}
          <div className="form-group">
            <label htmlFor="suggestion-title">{t('suggestion.labels.title')} *</label>
            <input
              id="suggestion-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('suggestion.placeholders.title')}
              className="form-input"
              disabled={isLoading}
              maxLength={MAX_SUGGESTION_TITLE_LENGTH}
              required
            />
            <span className="char-count">{title.length}/{MAX_SUGGESTION_TITLE_LENGTH}</span>
          </div>

          {/* Message Textarea */}
          <div className="form-group">
            <label htmlFor="suggestion-message">{t('suggestion.labels.message')} *</label>
            <textarea
              id="suggestion-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('suggestion.placeholders.message')}
              className="form-textarea"
              rows={6}
              disabled={isLoading}
              maxLength={MAX_SUGGESTION_MESSAGE_LENGTH}
              required
            />
            <span className="char-count">{message.length}/{MAX_SUGGESTION_MESSAGE_LENGTH}</span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="suggestion-error">
              <Icon name="warning" />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="suggestion-modal__actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isLoading || !title.trim() || !message.trim() || message.length < MIN_SUGGESTION_MESSAGE_LENGTH}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="small" color="white" hideMessage />
                  {t('suggestion.actions.sending')}
                </>
              ) : (
                <>
                  <Icon name="send" />
                  {t('suggestion.actions.submit')}
                </>
              )}
            </button>
          </div>

          {/* Helper Text */}
          <p className="suggestion-modal__helper">
            {t('suggestion.helper')}
          </p>
        </form>
      )}
    </Modal>
  );
};

export default SuggestionModal;
